import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { Animal } from '../data/types';
import './AddAnimalModal.css';

interface AddAnimalModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (animal: Animal) => void;
}

export function AddAnimalModal({ isOpen, onClose, onSubmit }: AddAnimalModalProps) {
    const [name, setName] = useState('');
    const [notes, setNotes] = useState('');
    const [feedingInterval, setFeedingInterval] = useState('2');

    const handleSubmit = () => {
        if (!name.trim()) {
            alert('Please enter a name');
            return;
        }

        const interval = parseFloat(feedingInterval);
        if (!interval || interval <= 0) {
            alert('Please enter a valid feeding interval');
            return;
        }

        const now = Date.now();
        const animal: Animal = {
            id: uuidv4(),
            name: name.trim(),
            notes: notes.trim() || undefined,
            feedingIntervalHours: interval,
            createdAt: now,
            updatedAt: now,
            status: 'active',
        };

        onSubmit(animal);

        // Reset form
        setName('');
        setNotes('');
        setFeedingInterval('2');
        onClose();
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal active" onClick={handleBackdropClick}>
            <div className="modal-content">
                <div className="modal-header">
                    <h2>Add Foster Animal</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="form-group">
                    <label className="form-label">Name</label>
                    <input
                        type="text"
                        className="form-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g., Mochi"
                        autoFocus
                    />
                </div>

                <div className="form-group">
                    <label className="form-label">
                        Feeding Interval <span className="unit">(hours)</span>
                    </label>
                    <div className="interval-presets">
                        {[1, 2, 3, 4, 6, 8].map((hours) => (
                            <div
                                key={hours}
                                className={`interval-option ${feedingInterval === hours.toString() ? 'selected' : ''}`}
                                onClick={() => setFeedingInterval(hours.toString())}
                            >
                                {hours}h
                            </div>
                        ))}
                    </div>
                    <input
                        type="number"
                        className="form-input"
                        value={feedingInterval}
                        onChange={(e) => setFeedingInterval(e.target.value)}
                        placeholder="Custom interval"
                        step="0.5"
                        min="0.5"
                    />
                </div>

                <div className="form-group">
                    <label className="form-label">
                        Notes <span className="unit">(optional)</span>
                    </label>
                    <textarea
                        className="form-textarea"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Age, intake notes, special needs..."
                        rows={3}
                    />
                </div>

                <div className="info-box">
                    <div className="info-icon">💡</div>
                    <div className="info-text">
                        You can add the first feeding and weight after creating the animal
                    </div>
                </div>

                <button className="submit-btn" onClick={handleSubmit}>
                    Add Animal
                </button>
            </div>
        </div>
    );
}